import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  useAssignProject,
  useRequirementScopeSuggestions,
} from '../../../hooks/useProjectAssign.js';
import {
  MAX_ASSIGN_ROWS,
  type AssignKind,
  type AssignResultRow,
  type AssignSkippedArtifact,
} from '../../../types/project-assign.js';
import { BACKLOG_BASE } from '../project/ChildTicketsModal.js';
import { parseAssignRows, toAssignRows } from './parse-rows.js';
import { ASSIGN_KIND_TONES, AssignKindToggle } from './AssignKindToggle.js';
import { hasEmptySkipReason } from './RequirementScopeFields.js';
import { AssignParsePreview } from './AssignParsePreview.js';
import {
  ASSIGN_SCOPE_DECISION_ORIGIN_LABELS,
  ASSIGN_SKIPPABLE_ARTIFACT_LABELS,
} from '../../../types/project-assign.js';

/** @graph-connects none */
export interface ProjectAssignModalProps {
  /** モーダルの表示状態 */
  open: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
}

/**
 * 貼り付けが落ち着いたと見なすまでの待ち（ms）。
 *
 * 案件ごとに AI を 1 回呼ぶので、打っている最中の 1 文字ごとに投げない。
 * @graph-connects none
 */
const SUGGEST_DEBOUNCE_MS = 800;

/** 種別ごとの入力ガイド（プレースホルダと列の説明）。 @graph-connects none */
const KIND_GUIDE: Record<AssignKind, { columns: string; placeholder: string }> = {
  requirement: {
    columns: '案件番号 / 担当エンジニア',
    placeholder: ['150123\tred', '150124\tstein', 'AIRCLOSET-150125, dhik'].join('\n'),
  },
  development: {
    columns: '案件番号 / 担当エンジニア / レビュアー',
    placeholder: ['150123\tred\tryan', '150124\tstein\tjack'].join('\n'),
  },
};

/** 種別ごとの「アサインすると何が起きるか」の説明。送信前に副作用を読めるようにする。 @graph-connects none */
const KIND_EFFECTS: Record<AssignKind, string[]> = {
  requirement: [
    '「業務フロー図作成」「バリデーションリスト作成」「機能一覧作成」「総合テスト作成」子チケットの担当者に 担当エンジニア を設定します（いずれも無い案件は失敗します）',
    '「作らない成果物」は AI が案件の憲章から仮判断して初期値に入ります。最終的に決めるのは実行者で、プレビュー表の「変更」から案件ごとに直せます',
    '「作らない」とした成果物は、子チケットに担当者を立てず理由をコメントで残し、PI Lab も催促しなくなります（理由は必須）',
    '親チケットの種別が「アサイン前」なら「プロダクト一覧」へ変更します（アサイン後に埋める項目を表示させるため）',
    '案件 Slack チャンネルへ通知します（担当エンジニア / 要件定義レビュー担当(DLE) / PL / 主担当 を mention）',
  ],
  development: [
    '「基本設計」子チケットがあれば担当者に 担当エンジニア を設定します（子チケットが無くてもアサインは成功します）',
    '親チケットの 開発リーダー / アサイン状況=決定 / アサイン決定日=当日 を設定します',
    '親チケットの種別が「アサイン前」なら「プロダクト一覧」へ変更します（アサイン後に埋める項目を表示させるため）',
    '案件 Slack チャンネルへ通知します（担当エンジニア / 要件定義担当 / レビュアー / DLE / PL / 主担当 を mention）',
  ],
};

/**
 * 案件アサインモーダル。pill トグルで「要件定義アサイン」「開発アサイン」を切り替え、
 * 複数行の貼り付けでまとめてアサインする。
 *
 * 送信前に**パース結果のプレビュー**を出すのが要点。区切り（タブ/カンマ/スペース）を自動判定して
 * 列を割るため、列ズレに気づかないまま送ると別人を担当者に立ててしまう。行ごとの判定を見せて、
 * 不正な行がある限り送信ボタンを押せないようにする。
 *
 * 要件定義アサインでは「この成果物は作らない」を**案件ごとに**理由つきで宣言できる
 * （決めすぎないゲート）。初期値は AI の仮判断で、貼り付けが落ち着いたら案件ごとに 1 回ずつ
 * 判定を取る。**人が触った案件は後から来た仮判断で上書きしない**（打ち消されると直した意味が
 * 無くなる）。記録されるのは送信したものだけで、仮判断そのものは保存しない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサインフォーム。種別トグルと複数行入力・作らない成果物の宣言・パースプレビュー・行ごとの実行結果を表示する
 * @graph-connects useAssignProject [calls] 案件アサイン実行
 * @graph-connects parseAssignRows [calls] 複数行入力のパース
 * @graph-connects useRequirementScopeSuggestions [calls] 作らない成果物の AI 仮判断
 * @graph-connects AssignParsePreview [references] パース結果と案件ごとの作らない成果物
 */
export function ProjectAssignModal({ open, onClose }: ProjectAssignModalProps): ReactNode {
  const [kind, setKind] = useState<AssignKind>('requirement');
  const [text, setText] = useState('');
  const [scopeByKey, setScopeByKey] = useState<Record<string, AssignSkippedArtifact[]>>({});
  const [editedKeys, setEditedKeys] = useState<string[]>([]);
  const [openKey, setOpenKey] = useState<string | null>(null);
  // 仮判断を取りに行く案件（入力が落ち着いてから確定させる）。
  const [settledKeys, setSettledKeys] = useState('');
  const mutation = useAssignProject();
  const { reset: resetMutation } = mutation;

  // 開くたびに状態をリセットする（resetMutation は安定参照）。
  useEffect(() => {
    if (open) {
      setKind('requirement');
      setText('');
      setScopeByKey({});
      setEditedKeys([]);
      setOpenKey(null);
      // 前回の貼り付けの案件が残っていると、開いた直後に古い案件の仮判断を取りに行く。
      setSettledKeys('');
      resetMutation();
    }
  }, [open, resetMutation]);

  const parsed = useMemo(() => parseAssignRows(text, kind), [text, kind]);
  const validRows = parsed.filter((row) => row.error === undefined);
  const invalidCount = parsed.length - validRows.length;
  const overLimit = validRows.length > MAX_ASSIGN_ROWS;

  // 打っている最中に AI を投げないよう、入力が落ち着いてから案件キーを確定させる。
  const validKeys = validRows.map((row) => row.issueKey).join(',');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettledKeys(validKeys);
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [validKeys]);

  const suggestionKeys = useMemo(
    () =>
      kind === 'requirement' && settledKeys !== '' && !overLimit ? settledKeys.split(',') : [],
    [kind, settledKeys, overLimit],
  );
  const suggestions = useRequirementScopeSuggestions(suggestionKeys);
  const suggestionByKey = useMemo(
    () => Object.fromEntries((suggestions.data ?? []).map((item) => [item.issueKey, item])),
    [suggestions.data],
  );

  // 仮判断を初期値として入れる。**人が触った案件は上書きしない**（直した内容が
  // 後から来た判定に打ち消されると、直した意味が無くなる）。
  useEffect(() => {
    const fetched = suggestions.data;
    if (fetched === undefined) return;
    setScopeByKey((current) => {
      const next = { ...current };
      for (const item of fetched) {
        if (editedKeys.includes(item.issueKey)) continue;
        next[item.issueKey] = item.skipped;
      }
      return next;
    });
  }, [suggestions.data, editedKeys]);

  // 理由が空のままの宣言があるときは送らせない（API も弾くが、行ごとのエラーで返るより
  // 入力欄の横で気づかせる方が早い）。今の行に関係ない案件の宣言は見ない。
  const missingReason =
    kind === 'requirement' &&
    validRows.some((row) => hasEmptySkipReason(scopeByKey[row.issueKey] ?? []));
  const canSubmit =
    validRows.length > 0 &&
    invalidCount === 0 &&
    !overLimit &&
    !missingReason &&
    !mutation.isPending;
  const tone = ASSIGN_KIND_TONES[kind];
  const results = mutation.data?.results;

  const changeScope = (issueKey: string, next: AssignSkippedArtifact[]) => {
    setScopeByKey((current) => ({ ...current, [issueKey]: next }));
    setEditedKeys((current) => (current.includes(issueKey) ? current : [...current, issueKey]));
  };

  const submit = () => {
    // 仮判断は id だけを送り返す（`toAssignRows` が拾う）。**宣言を直した案件でも送る**——
    // 直した内容と仮判断を突き合わせるには、そのとき見ていた仮判断が分かる必要がある。
    mutation.mutate({ kind, rows: toAssignRows({ parsed, kind, scopeByKey, suggestionByKey }) });
  };

  return (
    // size の既定（lg=32rem）だと副作用の説明文が折り返して読みにくいので、説明文 1 行が
    // 収まる 4xl（56rem）まで広げる。`full`（6xl）は入力欄 2〜3 列に対して広すぎる。
    <Modal open={open} onClose={onClose} title="案件をアサインする" size="lg" className="max-w-4xl">
      <div className="space-y-4">
        <AssignKindToggle
          value={kind}
          onChange={(next) => {
            setKind(next);
            // 構成の宣言は要件定義アサインだけのものなので、種別を変えたら捨てる
            // （残すと開発アサインで弾かれる行が出る）。
            setScopeByKey({});
            setEditedKeys([]);
            setOpenKey(null);
            // 列数の期待値が変わるので、種別を変えたら結果表示を畳む（前の種別の結果が残ると
            // 「今の入力に対する結果」と読み違える）。入力テキストは消さない（列を足すだけで済む）。
            resetMutation();
          }}
          disabled={mutation.isPending}
        />

        <ul className="space-y-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3 text-xs text-[var(--color-text-secondary)]">
          {KIND_EFFECTS[kind].map((effect) => (
            <li key={effect}>・{effect}</li>
          ))}
        </ul>

        <div className="space-y-1.5">
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
            style={{ backgroundColor: tone.pillBg, color: tone.pillText }}
          >
            {KIND_GUIDE[kind].columns}
          </span>
          <Textarea
            rows={8}
            value={text}
            placeholder={KIND_GUIDE[kind].placeholder}
            disabled={mutation.isPending}
            onChange={(e) => {
              setText(e.target.value);
            }}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 font-mono text-sm"
          />
          <p className="text-xs text-[var(--color-text-muted)]">
            1 行 = 1 案件。区切りはタブ / カンマ / スペースのいずれでも読み取ります（最大{' '}
            {MAX_ASSIGN_ROWS} 行）。
          </p>
        </div>

        {parsed.length > 0 && results === undefined && (
          <AssignParsePreview
            rows={parsed}
            kind={kind}
            overLimit={overLimit}
            scopeByKey={scopeByKey}
            suggestionByKey={suggestionByKey}
            suggesting={suggestions.isFetching}
            openKey={openKey}
            onToggleOpen={(issueKey) => {
              setOpenKey((current) => (current === issueKey ? null : issueKey));
            }}
            onChangeScope={changeScope}
            disabled={mutation.isPending}
          />
        )}

        {suggestions.isError && kind === 'requirement' && (
          <p className="text-xs text-[var(--color-warning)]">
            作らない成果物の AI
            仮判断を取得できませんでした。プレビュー表の「変更」から自分で選べます。
          </p>
        )}

        {mutation.isError && (
          <p className="rounded-md bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
            アサインに失敗しました: {mutation.error.message}
          </p>
        )}

        {results !== undefined && <AssignResults results={results} />}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={mutation.isPending}>
            {results === undefined ? 'キャンセル' : '閉じる'}
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!canSubmit}>
            {mutation.isPending ? 'アサイン中…' : `${String(validRows.length)} 件をアサインする`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** 実行結果（行ごとの成否）。 @graph-connects none */
function AssignResults({ results }: { results: readonly AssignResultRow[] }): ReactNode {
  const succeeded = results.filter((r) => r.ok).length;
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-[var(--color-text-primary)]">
        {succeeded} / {results.length} 件のアサインが成功しました
      </p>
      <div className="max-h-56 overflow-auto rounded-md border border-[var(--color-border)]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
            <tr className="text-left text-[var(--color-text-muted)]">
              <th className="px-3 py-2 font-medium">案件</th>
              <th className="px-3 py-2 font-medium">結果</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row) => (
              <tr key={row.issueKey} className="border-t border-[var(--color-border)] align-top">
                <td className="px-3 py-2 font-mono">
                  <a
                    href={`${BACKLOG_BASE}${row.issueKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {row.issueKey}
                  </a>
                </td>
                <td className="px-3 py-2">
                  {row.ok ? (
                    <span className="text-[var(--color-success)]">
                      ✓ アサイン完了
                      {row.assignedChildIssueKeys !== undefined &&
                        `（子チケット ${row.assignedChildIssueKeys.join(' / ')}）`}
                      {/* 宣言が記録されたことをここで確かめる。返っていなければ効いていない。 */}
                      {row.declaredSkipped !== undefined && (
                        <span className="ml-2 text-[var(--color-text-muted)]">
                          作らない:{' '}
                          {row.declaredSkipped
                            .map((artifact) => ASSIGN_SKIPPABLE_ARTIFACT_LABELS[artifact])
                            .join(' / ')}
                        </span>
                      )}
                      {/* 仮判断をそのまま通したか直したかが記録されたことをここで確かめる。 */}
                      {row.scopeOrigin !== undefined && (
                        <span className="ml-2 text-[var(--color-text-muted)]">
                          （{ASSIGN_SCOPE_DECISION_ORIGIN_LABELS[row.scopeOrigin]}）
                        </span>
                      )}
                      {!row.notified && (
                        <span className="ml-2 text-[var(--color-warning)]">
                          Slack 通知はできませんでした
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[var(--color-error)]">
                      ✕ {row.error ?? '失敗'}
                      {row.assignedChildIssueKeys !== undefined && (
                        <span className="ml-2 text-[var(--color-warning)]">
                          （子チケット {row.assignedChildIssueKeys.join(' / ')} の担当者は変更済み）
                        </span>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
